import ReminderCasePage from "@/pages/reminder-case";
import { PersonalRemindersProvider } from "@/lib/PersonalRemindersContext";
import { CurrentProfileProvider, useCurrentProfile } from "@/lib/CurrentProfileContext";
import { TeamProvider } from "@/lib/useTeam";
import { CaseTasksProvider } from "@/lib/CaseTasksContext";
import { NewTaskToast } from "@/components/tasks/NewTaskToast";
import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import HomePage from "@/pages/index";
import CasesPage from "@/pages/cases";
import CaseDetailPage from "@/pages/case/[id]";
import KanbanPage from "@/pages/kanban";
import CalendarPage from "@/pages/calendar";
import ProspectsPage from "@/pages/prospects";
import FeedbackPage from "@/pages/feedback";
import PublicFeedbackPage from "@/pages/feedback-public";
import { ProspectsProvider, ProspectsNavLabel } from "@/lib/ProspectsContext";
import ImportPage from "@/pages/import";
import TemplatesPage from "@/pages/templates";
import LoginPage from "@/pages/login";
import LoginPasswordPage from "@/pages/login-pass";
import PublicSignPage from "@/pages/sign/[token]";
import StatsPage from "@/pages/stats";
import ManagerPage from "@/pages/manager";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { BrandProvider, useBrand } from "@/lib/BrandContext";
import { NotificationsProvider } from "@/lib/NotificationsContext";
import { SelectedCentersProvider } from "@/lib/SelectedCentersContext";
import { ClientTypeFilterProvider } from "@/lib/ClientTypeFilterContext";
import { ClientTypeFilterDropdown } from "@/components/ui/ClientTypeFilterDropdown";
import { ProtectedRoute } from "@/components/ProtectedRoute";

const AUTO_REFRESH_INTERVAL_MS = 2 * 60 * 60 * 1000;

function setupAutoRefresh() {
  if (!import.meta.env.PROD || typeof window === "undefined") return;

  let lastSignature: string | null = null;

  const checkForUpdate = async () => {
    try {
      const response = await fetch("/", { method: "HEAD", cache: "no-store" });
      const etag = response.headers.get("etag");
      const lastModified = response.headers.get("last-modified");
      const signature = etag ?? lastModified ?? null;

      if (!signature) return;
      if (!lastSignature) {
        lastSignature = signature;
        return;
      }
      if (signature !== lastSignature) {
        window.location.reload();
      }
    } catch {
      // Ignore network errors to avoid noisy logs for users
    }
  };

  checkForUpdate();
  setInterval(checkForUpdate, AUTO_REFRESH_INTERVAL_MS);
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isActive = pathname === to || pathname.startsWith(to + "/");
  return (
    <Link
      to={to}
      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
        isActive
          ? "bg-indigo-50 text-indigo-700"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </Link>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const { brand, setBrand, brandConfig, isRestricted } = useBrand();
  const { isManager } = useCurrentProfile();
  const { pathname } = useLocation();
  const isKanbanRoute = pathname === "/kanban" || pathname.startsWith("/kanban/");

  return (
    <div className="min-h-screen">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container py-3 px-4 sm:px-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Logo / Nom de marque */}
            <Link to="/" className="text-lg font-bold text-gray-900 hover:text-indigo-600 transition-colors">
              {brandConfig.eventName}
            </Link>
            
            {/* Switch de marque (caché si utilisateur restreint à une marque) */}
            {!isRestricted && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBrand('smile')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    brand === 'smile'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Smile
                </button>
                <button
                  onClick={() => setBrand('laser')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    brand === 'laser'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Laser
                </button>
                <button
                  onClick={() => setBrand('vertical')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    brand === 'vertical'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Vertical
                </button>
              </div>
            )}

            {isKanbanRoute && user && <ClientTypeFilterDropdown />}
          </div>
          <nav className="flex max-w-full items-center gap-1 overflow-x-auto whitespace-nowrap pb-1" aria-label="Navigation principale">
            {user && (
              <>
                <NavLink to="/kanban">Kanban</NavLink>
                {brand === "smile" && <NavLink to="/prospects"><ProspectsNavLabel /></NavLink>}
                {brand === "smile" && import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && <NavLink to="/feedback">Feedback</NavLink>}
                <NavLink to="/calendar">Calendrier</NavLink>
                <NavLink to="/templates">Templates</NavLink>
                <NavLink to="/stats">Stats</NavLink>
                {isManager && <NavLink to="/manager">Manager</NavLink>}
                
                <div className="ml-4 pl-4 border-l border-gray-300 flex items-center gap-3">
                  <span className="hidden 2xl:inline text-xs text-gray-600">{user.email}</span>
                  <button
                    onClick={() => signOut()}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Déconnexion
                  </button>
                </div>
              </>
            )}
          </nav>
        </div>
      </header>
      <main>{children}</main>
      {user && <NewTaskToast />}
    </div>
  );
}

// Composant intermédiaire pour passer l'email utilisateur au BrandProvider
function AppWithBrand() {
  const { user } = useAuth();
  
  return (
    <ClientTypeFilterProvider>
      <NotificationsProvider>
        <SelectedCentersProvider>
          <BrandProvider userEmail={user?.email}>
            <ProspectsProvider>
            <Routes>
              {/* Routes publiques (sans Layout) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login-pass" element={<LoginPasswordPage />} />
              <Route path="/sign/:token" element={<PublicSignPage />} />
              <Route path="/avis/:token" element={<PublicFeedbackPage />} />

              {/* Routes protégées (avec Layout) */}
              <Route
                path="/*"
                element={
                  <Layout>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/kanban" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/home"
                        element={
                          <ProtectedRoute>
                            <HomePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/cases"
                        element={
                          <ProtectedRoute>
                            <CasesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/case/:id"
                        element={
                          <ProtectedRoute>
                            <CaseDetailPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/kanban"
                        element={
                          <ProtectedRoute>
                            <KanbanPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/relances"
                        element={<Navigate to="/kanban" replace />}
                      />
                      <Route
                        path="/prospects"
                        element={<ProtectedRoute><ProspectsPage /></ProtectedRoute>}
                      />
                      <Route path="/reminders/case/:id" element={<ProtectedRoute><ReminderCasePage /></ProtectedRoute>} />
                      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
                      <Route
                        path="/calendar"
                        element={
                          <ProtectedRoute>
                            <CalendarPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/import"
                        element={
                          <ProtectedRoute>
                            <ImportPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/templates"
                        element={
                          <ProtectedRoute>
                            <TemplatesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/manager"
                        element={
                          <ProtectedRoute>
                            <ManagerPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/stats"
                        element={
                          <ProtectedRoute>
                            <StatsPage />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Layout>
                }
              />
            </Routes>
            </ProspectsProvider>
          </BrandProvider>
        </SelectedCentersProvider>
      </NotificationsProvider>
    </ClientTypeFilterProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrentProfileProvider>
          <TeamProvider>
            <CaseTasksProvider>
              <PersonalRemindersProvider><AppWithBrand /></PersonalRemindersProvider>
            </CaseTasksProvider>
          </TeamProvider>
        </CurrentProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("Root element #root not found");
const root = createRoot(container);
root.render(<App />);

setupAutoRefresh();
